import { useState } from "react";
import type { ReactNode } from "react";

import { AlertTriangle, Loader2 } from "lucide-react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";
import { buttonVariants } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/utils";

export interface ConfirmDialogProps {
  /** Trigger a renderizar — típicamente un `<Button>` o un item de menú. */
  trigger: ReactNode;
  /** Título — display Fraunces. */
  title: string;
  /** Mensaje descriptivo con contexto de qué va a pasar. */
  description: ReactNode;
  /** Callback async. Si lanza, el dialog queda abierto para reintento. */
  onConfirm: () => void | Promise<void>;
  /** Copy del botón de confirmación. Default: "Confirmar". */
  confirmLabel?: string;
  /** Copy del botón de cancelar. Default: "Cancelar". */
  cancelLabel?: string;
  /** Tono del botón de confirmación — default `destructive`. */
  tone?: "destructive" | "primary";
  /** Icono — default `AlertTriangle`. */
  icon?: ReactNode;
}

/**
 * Dialog modal de confirmación para acciones destructivas o irreversibles.
 *
 * Pattern blindado:
 *   - Botón de confirmación muestra `Loader2` + bloquea mientras resuelve
 *     la promise de `onConfirm`.
 *   - Si `onConfirm` lanza, el dialog queda abierto y muestra el error —
 *     la usuaria puede reintentar o cancelar.
 *   - El cancel está siempre a la izquierda (orden macOS/web — menos
 *     chance de click accidental en destructive).
 *   - ESC + overlay click gated: solo cuando no está loading, para evitar
 *     que la usuaria pierda feedback de "esto está pasando".
 */
export function ConfirmDialog({
  trigger,
  title,
  description,
  onConfirm,
  confirmLabel = "Confirmar",
  cancelLabel = "Cancelar",
  tone = "destructive",
  icon,
}: ConfirmDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      setOpen(false);
    } catch {
      // Dejamos el dialog abierto — el llamador decide cómo comunicar
      // el error (toast desde el hook, p.ej.).
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        // No permitir cerrar mientras está loading.
        if (loading) return;
        setOpen(next);
      }}
    >
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div
            className={cn(
              "mb-2 flex h-10 w-10 items-center justify-center rounded-xl ring-1",
              tone === "destructive"
                ? "bg-destructive/10 text-destructive ring-destructive/15"
                : "bg-primary/10 text-primary ring-primary/15",
            )}
          >
            {icon ?? <AlertTriangle className="h-5 w-5" />}
          </div>
          <AlertDialogTitle className="font-display text-[1.25rem] font-medium tracking-display-tight">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[13.5px] leading-relaxed">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>{cancelLabel}</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              void handleConfirm();
            }}
            disabled={loading}
            className={cn(
              buttonVariants({ variant: tone === "destructive" ? "destructive" : "default" }),
              "gap-2",
            )}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Procesando…
              </>
            ) : (
              confirmLabel
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
